import React, { useEffect, useState } from 'react';
import { HiArrowsUpDown } from 'react-icons/hi2';
import { toast } from 'react-hot-toast';
import { updateRun } from '../../../api/runs';
import { useFormContext } from 'react-hook-form';
import { SessionAnalysisTypeUtils } from '../../../types/SessionAnalysisType';
import { EditOperationMode, isOperationLocked } from '../../SessionView/SessionDetails/SessionConfigs/SessionConfigs';

interface ConfigSelectorProps {
  run: any;
  config: any;
  session: any;
  setShowEditMenu: any;
  configs?: any[];
  sessions?: any[];
  invalidateRuns?: any;
  configMovesToSave?: { [key: string]: () => Promise<void> };
  editOpMode?: EditOperationMode;
  setEditOpMode?: (mode: EditOperationMode) => void;
}

const ConfigSelector: React.FC<ConfigSelectorProps> = ({
  run,
  config,
  session,
  setShowEditMenu,
  configs,
  sessions,
  invalidateRuns,
  configMovesToSave,
  editOpMode,
  setEditOpMode,
}) => {
  const { getValues, setValue } = useFormContext();

  const [selectedConfig, setSelectedConfig] = useState(config);
  const [selectedSession, setSelectedSession] = useState(session);

  useEffect(() => {
    setSelectedConfig(config);

    if (session && sessions) {
      const currentSession = sessions.find((s: any) => s.id === session.id);
      setSelectedSession(currentSession);
    }
  }, [session, config]);

  const saveConfigChange = async (newConfigId: any) => {
    await updateRun(run.id, { id: run.id, config_id: newConfigId });
  };

  const moveRun = async (newConfigId: any) => {
    if (!configs) return;

    const fromConfigIndex = configs.findIndex((c: any) => c.id === config.id);
    const toConfigIndex = configs.findIndex((c: any) => c.id === newConfigId);

    // Get current runs from both configs
    const fromConfigRuns = getValues(`configs.${fromConfigIndex}.runs`) || [];
    const toConfigRuns = getValues(`configs.${toConfigIndex}.runs`) || [];

    // Find and remove the run from the source config
    const fromRunIndex = fromConfigRuns.findIndex((r: any) => r.id === run.id);
    if (fromRunIndex !== -1) {
      const updatedFromRuns = [...fromConfigRuns];
      updatedFromRuns.splice(fromRunIndex, 1);
      setValue(`configs.${fromConfigIndex}.runs`, updatedFromRuns);
    }

    // Find the correct insertion index based on run_number
    const toRunIndex = toConfigRuns.findIndex((existingRun: any) => existingRun.run_number > run.run_number);

    let updatedToRuns;
    if (toRunIndex === -1) {
      // If no run has a higher run_number, append to the end
      updatedToRuns = [...toConfigRuns, run];
    } else {
      // Insert at the correct position
      updatedToRuns = [...toConfigRuns];
      updatedToRuns.splice(toRunIndex, 0, run);
    }

    setValue(`configs.${toConfigIndex}.runs`, updatedToRuns);

    // Add the saveConfigChange function to the configMovesToSave array if provided
    // Key by run.id (not newConfigId) so multiple runs moving to the same config don't overwrite each other
    if (configMovesToSave) {
      configMovesToSave[run.id] = () => saveConfigChange(newConfigId);
    }
  };

  const handleConfigChange = async (newConfigId: any) => {
    const availableConfigs = selectedSession?.configs || configs;
    const newConfig = availableConfigs?.find((c: any) => c.id === newConfigId);

    setSelectedConfig(newConfig);
    // Update the run's config_id
    if (run.id) {
      try {
        if (sessions) {
          await saveConfigChange(newConfigId);
          invalidateRuns();
        } else {
          if (isOperationLocked(editOpMode, 'run_move')) return;
          setEditOpMode?.('run_move');
          await moveRun(newConfigId);
        }

        setShowEditMenu(false);
      } catch (error) {
        console.error(error);
        toast.error('Error updating run config');
        // Revert the selection if the API call fails
        setSelectedConfig(config);
      }
    }
  };

  const handleSessionChange = async (newSessionId: any) => {
    const newSession = sessions?.find((s) => s.id === newSessionId);
    setSelectedSession(newSession);
    setSelectedConfig(null);
  };

  // Filter sessions to only show those that are valid (analysis_type >= 300)
  const validSessions = sessions?.filter((s: any) => SessionAnalysisTypeUtils.isSession(s.analysis_type));

  return (
    <>
      {validSessions && validSessions.length > 0 && (
        <>
          <div className="cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700">
            <div className="cr-flex cr-items-center cr-gap-2">
              <HiArrowsUpDown className="cr-size-4" />
              <span>Move to Session:</span>
            </div>
            <select
              className="cr-ml-4 cr-w-11/12 cr-rounded cr-border cr-border-gray-300
                                cr-bg-white cr-px-2 cr-py-1 cr-text-xs"
              value={selectedSession?.id || ''}
              onChange={(e) => handleSessionChange(e.target.value)}
            >
              <option key={null} value={''}>
                Select a Session
              </option>
              {validSessions
                ?.sort((a: any, b: any) => new Date(b.date || '').getTime() - new Date(a.date || '').getTime())
                ?.map((s: any) => (
                  <option key={s.id} value={s.id}>
                    {s.description}
                  </option>
                ))}
            </select>
          </div>
          {selectedSession && (
            <div className="cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700">
              <div className="cr-flex cr-items-center cr-gap-2">
                <HiArrowsUpDown className="cr-size-4" />
                <span>Move to Config:</span>
              </div>
              <select
                className="cr-ml-4 cr-w-11/12 cr-rounded cr-border cr-border-gray-300
                                  cr-bg-white cr-px-2 cr-py-1 cr-text-xs"
                value={selectedConfig?.id || ''}
                onChange={(e) => handleConfigChange(e.target.value)}
              >
                <option key={null} value={''}>
                  Select a Config
                </option>
                {selectedSession.configs?.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.letter} - {c.description}
                  </option>
                ))}
              </select>
            </div>
          )}
        </>
      )}
      {selectedConfig && configs && configs.length > 0 && (
        <div className={`cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700 ${isOperationLocked(editOpMode, 'run_move') ? 'cr-opacity-40' : ''}`}>
          <div className="cr-flex cr-items-center cr-gap-2">
            <HiArrowsUpDown className="cr-size-4" />
            <span>Move to Config:</span>
          </div>
          <select
            className="cr-ml-4 cr-w-11/12 cr-rounded cr-border cr-border-gray-300
                              cr-bg-white cr-px-2 cr-py-1 cr-text-xs"
            value={selectedConfig.id}
            onChange={(e) => handleConfigChange?.(e.target.value)}
            disabled={isOperationLocked(editOpMode, 'run_move')}
            title={isOperationLocked(editOpMode, 'run_move') ? 'Save pending changes first' : undefined}
          >
            {configs?.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.letter} - {c.description}
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  );
};

export default ConfigSelector;
